import React from "react";
import PropTypes from "prop-types";

/**
 * DesktopDot renders pagination dots with an animated progress indicator for a carousel.
 * @param {object} props - Component props.
 * @param {number} props.currentSlide - The currently active slide index.
 * @param {number} props.totalSlides - Total number of slides.
 * @param {Function} props.onDotClick - Callback invoked with the index when a dot is clicked.
 * @param {boolean} props.isAutoplay - Whether the carousel is in autoplay mode.
 * @param {number} props.autoplaySpeed - Duration of each autoplay slide in milliseconds.
 * @returns {React.ReactElement|null} Dot navigation or null if only one slide.
 */
const DesktopDot = ({ currentSlide, totalSlides, onDotClick, isAutoplay = false, autoplaySpeed = 3000 }) => {
  if (totalSlides <= 1) return null;

  return (
    <div className="mt-16 flex items-center justify-center gap-8">
      <style>{`
        @keyframes fillProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      {Array.from({ length: totalSlides }).map((_, index) => {
        const isActive = index === currentSlide;

        /**
         * Handles dot click for this index.
         */
        const onThisDotClick = () => onDotClick(index);

        return (
          <button
            key={index}
            type="button"
            className="relative cursor-pointer overflow-hidden rounded-sm transition-all duration-300"
            style={{
              width: isActive ? "80px" : "32px",
              height: "4px",
              backgroundColor: "#CBD5E0",
              borderRadius: "5px",
            }}
            aria-label={`Go to slide ${index + 1}`}
            onClick={onThisDotClick}
          >
            {isActive && isAutoplay && (
              <div
                key={`progress-${currentSlide}`}
                className="absolute left-0 top-0 h-full rounded-sm bg-[#737530]"
                style={{
                  width: "0%",
                  animation: `fillProgress ${autoplaySpeed}ms linear forwards`,
                }}
              />
            )}
            {isActive && !isAutoplay && (
              <div className="absolute left-0 top-0 h-full rounded-sm bg-[#737530]" style={{ width: "100%" }} />
            )}
          </button>
        );
      })}
    </div>
  );
};

DesktopDot.propTypes = {
  currentSlide: PropTypes.number.isRequired,
  totalSlides: PropTypes.number.isRequired,
  onDotClick: PropTypes.func.isRequired,
  isAutoplay: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
};

export default DesktopDot;
